import { useCallback, useMemo, useState } from 'react'

import { useSafetyTranslation } from 'cng-common/features/translations'
import { Table } from 'core-components/library/list'
import type { IColumn } from 'core-components/library/list/types'
import { ModalDialog } from 'core-components/library/modalDialog'
import { IScoreCard } from 'partner360/features/keyInitiatives'
import { StrategicAlignmentPillar } from 'partner360/features/keyInitiatives'
import { scoreCards } from 'partner360/features/keyInitiatives/configs/config'
import { TRANSLATION_KEYS } from 'partner360/features/keyInitiatives/configs/translations'
import { ScoreCard } from 'partner360/features/keyInitiatives/ui/common/scoreCard/ScoreCard'
import { formatMoney } from 'partner360/features/keyInitiatives/ui/helpers/formatMoney'
import { QualityPillar } from 'partner360/features/keyInitiatives/ui/keyInitiativesQuality/components/qualityPillar/QualityPillar'
import { useDescriptionQualityColumns } from 'partner360/features/keyInitiatives/ui/keyInitiativesQuality/hooks/useDescriptionQualityColumns'
import { useFinancialTableConfig } from 'partner360/features/keyInitiatives/ui/keyInitiativesQuality/hooks/useFinancialColumns'

import type { ICockpitExportView } from '../../../../api'
import type { TCockpitInitiativesQuality } from '../../../../lib'
import {
  type TCockpitExportSlot,
  useFillCockpitExportSlot,
} from '../../../components/cockpitExport'
import {
  CockpitTableColumnTitle,
  CockpitTableRowTitle,
} from '../../../components/cockpitTableTitle'

import { getInitiativesQualityPillarsExport } from './getInitiativesQualityPillarsExport'

import styles from './CockpitInitiativesQuality.module.scss'

const KEYS = TRANSLATION_KEYS.quality

/** The editor the Planning activities widget opens from a detail row. The cockpit tile reads
 *  data, it does not edit initiatives, so rows carry no actions here and this is never called —
 *  the column configs simply require a handler. */
const noEditor = () => undefined

const withCockpitTableTitles = <T,>(columns: IColumn<T>[]): IColumn<T>[] =>
  columns.map((column, index) => {
    const titleRender = column.titleRender
    const renderFunction = column.renderFunction
    const titledColumn: IColumn<T> = {
      ...column,
      titleRender: (props) => (
        <CockpitTableColumnTitle>
          {titleRender ? titleRender(props) : column.title}
        </CockpitTableColumnTitle>
      ),
    }

    if (index !== 0 || !renderFunction) {
      return titledColumn
    }

    return {
      ...titledColumn,
      renderFunction: (item, tableColumn, rowAttrs) => (
        <CockpitTableRowTitle>{renderFunction(item, tableColumn, rowAttrs)}</CockpitTableRowTitle>
      ),
    }
  })

interface ICockpitInitiativesQualityProps {
  quality: TCockpitInitiativesQuality
  currency: string
  exportSlot?: TCockpitExportSlot
}

/**
 * The three plan-quality pillars of the Planning activities widget, rendered inside the cockpit's
 * Initiatives tile: Financial plan coverage, Description quality, Strategic growth areas.
 *
 * Pillars, scoring dialog, detail tables and their columns are the widget's own components, fed
 * by the widget's own calculation — a score and a list read identically in both places. Two
 * things the planning screen owns are left out: a detail row opens no initiative editor, and a
 * business priority is not clickable (there it applies a quick filter to the widget's own list,
 * which the tile does not have).
 */
export const CockpitInitiativesQuality = ({
  quality,
  currency,
  exportSlot,
}: ICockpitInitiativesQualityProps) => {
  const translate = useSafetyTranslation()
  const [dialogScoreCard, setDialogScoreCard] = useState<IScoreCard | null>(null)
  const [isFinCoverageDetailsVisible, setIsFinCoverageDetailsVisible] = useState(false)
  const [isDescriptionQualityDetailsVisible, setIsDescriptionQualityDetailsVisible] =
    useState(false)
  /** The Strategic card keeps its panels to itself and reports them, for the export alone. */
  const [strategicView, setStrategicView] = useState({
    isBreakdownVisible: false,
    isInitiativesVisible: false,
  })

  const { financialTargetGapColumns } = useFinancialTableConfig({
    activeCurrency: currency,
    onEditInitiative: noEditor,
  })
  const { descriptionQualityGapColumns } = useDescriptionQualityColumns({
    onEditInitiative: noEditor,
  })
  const cockpitFinancialTargetGapColumns = useMemo(
    () => withCockpitTableTitles(financialTargetGapColumns),
    [financialTargetGapColumns]
  )
  const cockpitDescriptionQualityGapColumns = useMemo(
    () => withCockpitTableTitles(descriptionQualityGapColumns),
    [descriptionQualityGapColumns]
  )

  /** The cards as they stand, each panel only while the user has it open. */
  const exportView = useCallback(
    (): ICockpitExportView => ({
      key: 'initiativesQuality',
      controls: [],
      pillars: getInitiativesQualityPillarsExport({
        quality,
        currency,
        translate,
        open: {
          financialPlanCoverageTable: isFinCoverageDetailsVisible,
          descriptionQualityTable: isDescriptionQualityDetailsVisible,
          strategicBreakdown: strategicView.isBreakdownVisible,
          strategicTable: strategicView.isInitiativesVisible,
        },
      }),
    }),
    [
      quality,
      currency,
      translate,
      isFinCoverageDetailsVisible,
      isDescriptionQualityDetailsVisible,
      strategicView,
    ]
  )

  useFillCockpitExportSlot(exportSlot, exportView)

  return (
    <div className={styles.root}>
      <QualityPillar
        title={KEYS.financialPlanCoverage.title}
        score={quality.financialPlanCoverage.score}
        isNotApplicable={quality.financialPlanCoverage.score === null}
        notApplicableText={KEYS.financialPlanCoverage.notApplicable}
        band={quality.financialPlanCoverage.band}
        description={KEYS.financialPlanCoverage.description}
        descriptionReplaceParams={[
          { key: '{goalCoverage}', value: String(quality.financialPlanCoverage.goalCoverage) },
          {
            key: '{incrementalCoverage}',
            value: String(quality.financialPlanCoverage.incrementalCoverage),
          },
          {
            key: '{IncrementalPlanSum}',
            value: formatMoney(quality.financialPlanCoverage.incrementalPlanSum, currency),
          },
          {
            key: '{IncrementalInitiativesSum}',
            value: formatMoney(quality.financialPlanCoverage.incrementalInitiativesSum, currency),
          },
        ]}
        actionText={
          quality.financialPlanCoverage.initiativesWithIncremental.length > 0
            ? isFinCoverageDetailsVisible
              ? KEYS.actions.hideDetailsFin
              : KEYS.actions.showDetailsFin
            : undefined
        }
        actionReplaceParams={[
          {
            key: '{count}',
            value: String(quality.financialPlanCoverage.initiativesWithIncremental.length),
          },
        ]}
        isActionActive={isFinCoverageDetailsVisible}
        onAction={() => setIsFinCoverageDetailsVisible((value) => !value)}
        onHowScore={() => setDialogScoreCard(scoreCards[0])}
      >
        {isFinCoverageDetailsVisible && (
          <div className={styles.details}>
            <Table
              items={quality.financialPlanCoverage.initiativesWithIncremental}
              columns={cockpitFinancialTargetGapColumns}
              keyField={'EntryId'}
              header
            />
          </div>
        )}
      </QualityPillar>

      <QualityPillar
        title={KEYS.descriptionQuality.title}
        score={quality.descriptionQuality.score}
        band={quality.descriptionQuality.band}
        description={KEYS.descriptionQuality.description}
        descriptionReplaceParams={[
          {
            key: '{wellDescribedCount}',
            value: String(quality.descriptionQuality.wellDescribedCount),
          },
          { key: '{totalCount}', value: String(quality.descriptionQuality.totalCount) },
        ]}
        successText={
          quality.descriptionQuality.lowScoredCount === 0
            ? KEYS.descriptionQuality.success
            : undefined
        }
        actionText={
          quality.descriptionQuality.lowScoredCount > 0
            ? isDescriptionQualityDetailsVisible
              ? KEYS.actions.hideDetailsDesc
              : KEYS.actions.showDetailsDesc
            : undefined
        }
        actionReplaceParams={[
          { key: '{count}', value: String(quality.descriptionQuality.lowScoredCount) },
        ]}
        isActionActive={isDescriptionQualityDetailsVisible}
        onAction={() => setIsDescriptionQualityDetailsVisible((value) => !value)}
        onHowScore={() => setDialogScoreCard(scoreCards[1])}
      >
        {isDescriptionQualityDetailsVisible && (
          <div className={styles.details}>
            <Table
              items={quality.descriptionQuality.lowScoredInitiatives}
              columns={cockpitDescriptionQualityGapColumns}
              keyField={'EntryId'}
              header
            />
          </div>
        )}
      </QualityPillar>

      <StrategicAlignmentPillar
        quality={quality.strategicGrowthAreas}
        onViewChange={setStrategicView}
      />

      <ModalDialog
        show={dialogScoreCard !== null}
        onClose={() => setDialogScoreCard(null)}
        contentProps={{ scoreCard: dialogScoreCard }}
        content={ScoreCard}
        size={'xs'}
      />
    </div>
  )
}

CockpitInitiativesQuality.displayName = 'CockpitInitiativesQuality'
